import { useCallback } from 'react';
import { URL, USR, TKN } from '@/modules/shared/lib/consts';
import { safeJsonParse, safeLocalRemoveAuth } from '@/modules/shared/lib/utils';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { appendAppVersionHeaders } from '@/modules/shared/lib/app-version';

export type RevalidarResultado =
  | { valido: true; retorno: Identity }
  | { valido: false; retorno?: any; erroRede?: boolean };

const _revalidarPuro = async (payload: any): Promise<RevalidarResultado> => {
  try {
    if (!payload || !payload.token) {
      safeLocalRemoveAuth();
      return { valido: false };
    }

    const payloadNormalizado: any =
      payload && typeof payload === "object" ? { ...payload } : payload;
    if (
      payloadNormalizado && typeof payloadNormalizado === "object" && payloadNormalizado.role === "PROFESSOR") {
      payloadNormalizado.utiliza_redacao = true;
    }

    const url = `${URL}/auth/revalidar`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${payloadNormalizado.token}`
    };
    const body = JSON.stringify(payloadNormalizado);

    let respostaStatus: number = 0;
    let respostaData: any = null;

    const headersComVersao = appendAppVersionHeaders(headers);

    if (Capacitor.isNativePlatform()) {
      const res = await CapacitorHttp.request({
        url,
        method: 'POST',
        headers: headersComVersao,
        data: body
      });
      respostaStatus = res.status;
      respostaData = res.data;
    } else {
      const res = await fetch(url, {
        method: 'POST',
        headers: headersComVersao,
        body
      });
      respostaStatus = res.status;
      try { respostaData = await res.json(); } catch { respostaData = null; }
    }

    if (respostaStatus === 401 || respostaStatus === 403) {
      safeLocalRemoveAuth();
      return { valido: false, retorno: respostaData };
    }

    if (respostaStatus !== 200 || !respostaData) {
      return { valido: false, retorno: respostaData, erroRede: true };
    }

    const envelope = respostaData?.data ?? respostaData;
    const retorno = (envelope && typeof envelope === 'object' && 'token' in envelope) ? envelope : null;

    const backendConfirmouInvalido = respostaStatus === 200 && respostaData && (respostaData.ok === false || respostaData.success === false || respostaData.valido === false);

    if (backendConfirmouInvalido || !retorno || !retorno.token || typeof retorno !== 'object') {
      if (backendConfirmouInvalido) {
        safeLocalRemoveAuth();
      }
      return { valido: backendConfirmouInvalido ? false : false, retorno: retorno ?? respostaData, erroRede: !backendConfirmouInvalido && !retorno };
    }

    const retornoCompleto = await complementarDadosProfessorSeNecessario(retorno as unknown as Identity, (path: string, body?: Record<string, unknown>) => fetchWrapper(path, body ?? {}, respostaData?.token ?? (retorno as any)?.token));

    try {
      localStorage.setItem(TKN, String(retornoCompleto.token ?? ""));
      localStorage.setItem(USR, JSON.stringify(retornoCompleto));
    } catch {
      safeLocalRemoveAuth();
      return { valido: false };
    }

    return { valido: true, retorno: retornoCompleto as Identity };
  } catch (err) {
    if (import.meta.env.DEV) {
    }
    return { valido: false, erroRede: true };
  }
};

const _normalizarBoolUtiliza = (v: unknown): boolean => {
  if (v === true || v === "true" || v === 1 || v === "1" || v === "S" || v === "s") return true;
  return false;
};

const complementarDadosProfessorSeNecessario = async <T extends Partial<Identity>>(
  identity: T,
  post: (path: string, body: Record<string, unknown>) => Promise<{ success: boolean; data?: any; err?: unknown }>
): Promise<T> => {
  if ((identity as any)?.role !== "PROFESSOR") return identity;
  if (typeof (identity as any)?.utiliza_redacao === "boolean") return identity;

  try {
    const res = await post("/proc/obter_professores", { id: String((identity as any)?.id ?? "") });
    const row = Array.isArray((res as any)?.data?.dados) ? (res.data as any).dados[0] : null;
    const obj = (row ?? (res as any)?.data ?? null) as Record<string, unknown> | null;
    if (!obj || typeof obj !== "object") return identity;
    const utiliza = "utiliza_redacao" in obj ? _normalizarBoolUtiliza(obj.utiliza_redacao) : (identity as any)?.utiliza_redacao;
    return {
      ...identity,
      utiliza_redacao: typeof utiliza === "boolean" ? utiliza : false,
    } as T;
  } catch {
    return identity;
  }
};

const fetchWrapper = async (path: string, body: Record<string, unknown>, token: string | undefined): Promise<{ success: boolean; data?: any; err?: unknown }> => {
  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(`${URL}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body ?? {}),
    });
    if (!r.ok) return { success: false, err: new Error("HTTP " + r.status) };
    let json = null;
    try { json = await r.json(); } catch {}
    return { success: true, data: json };
  } catch (err) {
    return { success: false, err };
  }
};

export const revalidarAuth = _revalidarPuro;

type UseAuthRevalidateOptions = {
  onInvalido?: () => void;
  onValido?: (retorno: Identity) => void;
};

export const useAuthRevalidate = (opts: UseAuthRevalidateOptions = {}) => {
  const revalidar = useCallback(async (): Promise<RevalidarResultado> => {
    const payload = safeJsonParse<any>(localStorage.getItem(USR));
    const resultado = await _revalidarPuro(payload);
    if (!resultado.valido && !resultado.erroRede) {
      opts.onInvalido?.();
    } else if (resultado.valido) {
      opts.onValido?.(resultado.retorno);
    }
    return resultado;
  }, [opts.onInvalido, opts.onValido]);

  return { revalidar };
};
